/**
 * Alert utility that provides a drop-in replacement for React Native's Alert.alert
 * with our custom themed alert component
 */

interface AlertButton {
  text: string;
  onPress?: () => void;
  style?: 'default' | 'cancel' | 'destructive';
}

interface AlertOptions {
  cancelable?: boolean;
  onDismiss?: () => void;
}

interface AlertType {
  type?: 'info' | 'success' | 'warning' | 'error';
}

class AlertUtility {
  private static showAlertCallback:
    | ((
        title: string,
        message?: string,
        buttons?: AlertButton[],
        options?: AlertOptions & AlertType,
      ) => void)
    | null = null;

  static setShowAlert(
    callback: (
      title: string,
      message?: string,
      buttons?: AlertButton[],
      options?: AlertOptions & AlertType,
    ) => void,
  ) {
    this.showAlertCallback = callback;
  }

  static alert(title: string, message?: string, buttons?: AlertButton[], options?: AlertOptions) {
    if (this.showAlertCallback) {
      this.showAlertCallback(title, message, buttons, options);
    } else {
      // Fallback to native Alert if custom alert not available
      const { Alert: NativeAlert } = require('react-native');
      NativeAlert.alert(title, message, buttons, options);
    }
  }

  // Convenience methods for common alert types
  static success(title: string, message?: string, onPress?: () => void) {
    this.alert(title, message, [{ text: 'OK', onPress }], { type: 'success' } as any);
  }

  static error(title: string, message?: string, onPress?: () => void) {
    this.alert(title, message, [{ text: 'OK', onPress }], { type: 'error' } as any);
  }

  static warning(title: string, message?: string, onPress?: () => void) {
    this.alert(title, message, [{ text: 'OK', onPress }], { type: 'warning' } as any);
  }

  static info(title: string, message?: string, onPress?: () => void) {
    this.alert(title, message, [{ text: 'OK', onPress }], { type: 'info' } as any);
  }

  static confirm(
    title: string,
    message?: string,
    onConfirm?: () => void,
    onCancel?: () => void,
    confirmText: string = 'Confirm',
    cancelText: string = 'Cancel',
    destructive: boolean = false,
  ) {
    this.alert(title, message, [
      { text: cancelText, onPress: onCancel, style: 'cancel' },
      {
        text: confirmText,
        onPress: onConfirm,
        style: destructive ? 'destructive' : 'default',
      },
    ]);
  }
}

export const Alert = AlertUtility;

/**
 * Legacy function interface for backward compatibility
 * Maps to Alert.alert for existing code that expects showAlert function
 */
export const showAlert = (title: string, message?: string, buttons?: AlertButton[]) => {
  Alert.alert(title, message, buttons);
};
